import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Interface untuk data tes yang tersimpan
interface TestData {
  date: string;
  score: string;
  level: string;
}

const FreeTestCBT: React.FC = () => {
  const navigate = useNavigate();

  // --- STATE ---
  const [showCountdown, setShowCountdown] = useState(false);
  const [count, setCount] = useState(3);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [testData, setTestData] = useState<TestData>({ date: '-', score: '-', level: '-' });

  useEffect(() => {
    checkTestStatus();
  }, []);

  // --- LOGIKA COUNTDOWN ---
  useEffect(() => {
    let timer: any;
    if (showCountdown && count > 0) {
      timer = setTimeout(() => setCount((prev) => prev - 1), 1000);
    } else if (showCountdown && count === 0) {
      timer = setTimeout(() => {
        localStorage.setItem('fluenesia_test_started', new Date().toISOString());
        navigate('/assessment/soal-test');
      }, 800);
    }
    return () => clearTimeout(timer);
  }, [showCountdown, count, navigate]);

  // --- LOGIKA STATUS TES ---
  const checkTestStatus = () => {
    if (typeof window !== 'undefined') {
      const hasTakenTest = localStorage.getItem('fluenesia_test_taken');
      if (hasTakenTest === 'true') {
        setTestData({
          date: localStorage.getItem('fluenesia_test_date') || '-',
          score: localStorage.getItem('fluenesia_test_score') || '-',
          level: localStorage.getItem('fluenesia_test_level') || '-',
        });
        setIsModalOpen(true);
        return false;
      }
    }
    return true;
  };

  const handleStartTest = () => {
    if (checkTestStatus()) {
      setShowCountdown(true);
    }
  };

  const handleResetTest = () => {
    if (window.confirm('Are you sure you want to retake the test? Previous results will be deleted.')) {
      localStorage.removeItem('fluenesia_test_taken');
      localStorage.removeItem('fluenesia_test_date');
      localStorage.removeItem('fluenesia_test_score');
      localStorage.removeItem('fluenesia_test_level');
      localStorage.removeItem('fluenesia_test_started');
      setIsModalOpen(false);
      alert('Test status reset.');
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] font-sans text-gray-900 pb-32 relative">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        
        body { font-family: 'Inter', sans-serif; }

        /* Animasi Countdown */
        @keyframes popIn {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.2); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        .animate-pop { animation: popIn 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
        
        /* Animasi Modal */
        .modal-content-animate { animation: modalSlideIn 0.3s ease-out; }
        @keyframes modalSlideIn { from { opacity: 0; transform: translateY(-30px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* --- OVERLAY COUNTDOWN --- */}
      {showCountdown && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-[#E50000] bg-opacity-95 backdrop-blur-sm">
          <div className="text-center">
            {count > 0 ? (
              <div key={count} className="animate-pop">
                <div className="text-9xl font-extrabold text-white drop-shadow-md mb-4">{count}</div>
                <p className="text-white text-xl font-medium tracking-widest uppercase">Get Ready</p>
              </div>
            ) : (
              <div className="animate-pop">
                <div className="text-8xl font-extrabold text-white drop-shadow-md mb-4">GO!</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* --- MODAL SUDAH TES (Logic Only) --- */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[9999] bg-black/50 flex justify-center items-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl modal-content-animate text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center text-2xl">⚠️</div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">Test Already Taken</h3>
            <div className="bg-gray-50 rounded-xl p-3 mb-4 text-sm text-left border border-gray-100">
              <div className="flex justify-between mb-1"><span className="text-gray-500">Score:</span><span className="font-bold text-gray-800">{testData.score}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Level:</span><span className="font-bold text-[#E50000]">{testData.level}</span></div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => navigate('/user/dashboard', { state: { tab: 'Assessment' } })} className="flex-1 bg-[#E50000] text-white py-2 rounded-lg font-bold text-sm">See Result</button>
              <button onClick={handleResetTest} className="flex-1 border border-gray-300 text-gray-600 py-2 rounded-lg font-bold text-sm">Reset</button>
            </div>
          </div>
        </div>
      )}

      {/* --- CONTENT START --- */}
      
      {/* 1. Header (Back Button) */}
      <div className="pt-6 px-5 mb-4">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-black font-semibold text-[15px]">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back
        </button>
      </div>

      <div className="px-5 space-y-6 max-w-[480px] mx-auto w-full">
        
        {/* 2. Pink Box (Placeholder Illustration) */}
        <div className="w-full h-48 bg-[#FFE5E5] rounded-[24px]"></div>

        {/* 3. Stats Card */}
        <div className="bg-white rounded-[24px] p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-gray-100 flex justify-between items-center h-[100px]">
          {/* Item 1 */}
          <div className="flex items-center gap-4 w-1/2">
            <div className="w-12 h-12 bg-gray-200 rounded-[10px] flex-shrink-0"></div>
            <div>
              <p className="text-[13px] text-gray-600 font-medium leading-tight mb-1">Number of Questions</p>
              <p className="text-[17px] font-bold text-black">15 Questions</p>
            </div>
          </div>
          
          {/* Divider (Optional/Invisible based on image, using margin instead) */}

          {/* Item 2 */}
          <div className="flex items-center gap-4 w-1/2 pl-2">
            <div className="w-12 h-12 bg-gray-200 rounded-[10px] flex-shrink-0"></div>
            <div>
              <p className="text-[13px] text-gray-600 font-medium leading-tight mb-1">Time Limit</p>
              <p className="text-[17px] font-bold text-black">20 Minutes</p>
            </div>
          </div>
        </div>

        {/* 4. Instructions Card (Red Header + White Body) */}
        <div className="rounded-[30px] overflow-hidden shadow-[0_4px_12px_rgba(0,0,0,0.05)] bg-white">
          
          {/* Red Header */}
          <div className="bg-[#D50000] p-6 pb-10 pt-7 text-white">
            <h2 className="text-[22px] font-bold mb-1 tracking-tight">Placement Test Instructions</h2>
            <p className="text-white/90 text-[13px] font-normal">Read these instructions to make sure everything runs smoothly.</p>
          </div>

          {/* White Body - Overlapping Upwards */}
          <div className="bg-white rounded-t-[30px] px-6 py-8 -mt-6 relative z-10">
            <ol className="list-decimal list-outside pl-4 space-y-2 text-[14px] text-gray-800 leading-[1.5]">
              <li className="pl-1">
                Log in to your Fluenesia account. If you don't have one yet, please sign up first.
              </li>
              <li className="pl-1">
                This test contains basic Indonesian language questions, including simple vocabulary and everyday expressions.
              </li>
              <li className="pl-1">
                Make sure your internet connection is stable before starting the test.
              </li>
              <li className="pl-1">
                If you want to skip a question, click "Mark for Review." You can return to it after completing all questions.
              </li>
              <li className="pl-1">
                When the time is up, your answers will be submitted automatically.
              </li>
              <li className="pl-1">
                Each section can only be taken once and cannot be repeated.
              </li>
              <li className="pl-1">
                Answer honestly to help us place you in the most suitable learning level. Good luck!
              </li>
            </ol>
          </div>
        </div>

      </div>

      {/* 5. Bottom Button */}
      <div className="fixed bottom-0 left-0 w-full bg-white/0 p-5 z-20">
        <div className="max-w-[480px] mx-auto">
          <button 
            onClick={handleStartTest}
            className="w-full bg-[#FF0000] hover:bg-[#D50000] text-white font-semibold text-[16px] py-4 rounded-[12px] shadow-lg transition-transform active:scale-[0.98]"
          >
            Start Test
          </button>
        </div>
      </div>

    </div>
  );
};

export default FreeTestCBT;